// Voice Tutor controls (§11.3) — talk to Professor Cogni.
// Tap to speak; 2s silence (or tap again) sends the transcript through the
// existing tutor flow (sendQuery → Pass 4); the reply is read back via premium
// Google TTS (Pro/Teams) with browser-TTS fallback.
//
// Two modes:
//   • Push-to-talk (default) — tap, speak, it sends and reads the reply.
//   • Hands-free (continuous) — after each reply finishes, listening auto-resumes
//     for a back-and-forth conversation. Toggle with the loop button.
//
// Gated to Pro/Teams (`voice_tutor` entitlement) behind the `tutor.voice` flag.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Square, Volume2, Repeat } from 'lucide-react';
import { useAuthStore, useTutorStore, useFeatureFlagStore } from '@shared/state';
import { hasFeature } from '@shared/services/featureGating';
import { RECALIBRATION_FLAGS } from '@shared/constants';
import { useSpeechRecognition, SPEECH_END_EVENT } from './useSpeechRecognition';
import { useReplyVoice } from './useReplyVoice';

function speakable(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' (code) ')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[*_`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function VoiceTutorControls() {
  const user = useAuthStore((s) => s.user);
  const voiceFlag = useFeatureFlagStore((s) => s.isEnabled(RECALIBRATION_FLAGS.VOICE_TUTOR));
  const sendQuery = useTutorStore((s) => s.sendQuery);
  const messages = useTutorStore((s) => s.currentSession?.messages ?? []);
  const isLoading = useTutorStore((s) => s.isLoading);

  const stt = useSpeechRecognition();
  const voice = useReplyVoice();
  const voiceRef = useRef(voice);
  voiceRef.current = voice;

  const [listening, setListening] = useState(false);
  const listeningRef = useRef(false);
  listeningRef.current = listening;

  const [continuous, setContinuous] = useState(false);
  const continuousRef = useRef(false);
  continuousRef.current = continuous;

  const lastSpokenRef = useRef<string | null>(null);
  const skipResumeRef = useRef(false); // suppress auto-resume after a manual stop
  const prevSpeakingRef = useRef(false);

  const entitled =
    !!user && hasFeature(user.subscription, 'voice_tutor') && voiceFlag && stt.isSupported;

  const beginListening = useCallback(() => {
    stt.resetTranscript();
    setListening(true);
    stt.startListening();
  }, [stt]);

  const submit = useCallback(
    (text: string) => {
      const q = text.trim();
      setListening(false);
      stt.stopListening();
      if (q) void sendQuery(q);
      stt.resetTranscript();
    },
    [sendQuery, stt],
  );

  // 2s-silence auto-send.
  useEffect(() => {
    if (!entitled) return;
    function onEnd(e: Event) {
      if (!listeningRef.current) return;
      submit((e as CustomEvent<{ transcript: string }>).detail?.transcript ?? '');
    }
    window.addEventListener(SPEECH_END_EVENT, onEnd);
    return () => window.removeEventListener(SPEECH_END_EVENT, onEnd);
  }, [entitled, submit]);

  // Read back each new assistant reply (premium TTS → browser fallback).
  useEffect(() => {
    if (!entitled) return;
    const last = messages[messages.length - 1];
    if (last && last.role === 'assistant' && last.messageId !== lastSpokenRef.current) {
      lastSpokenRef.current = last.messageId;
      voiceRef.current.speak(speakable(last.content));
    }
  }, [messages, entitled]);

  // Hands-free: when a reply finishes speaking, auto-resume listening.
  useEffect(() => {
    const wasSpeaking = prevSpeakingRef.current;
    prevSpeakingRef.current = voice.isSpeaking;
    if (!wasSpeaking || voice.isSpeaking) return; // only on speaking → done
    if (skipResumeRef.current) {
      skipResumeRef.current = false;
      return;
    }
    if (continuousRef.current && !isLoading && !listeningRef.current) {
      beginListening();
    }
  }, [voice.isSpeaking, isLoading, beginListening]);

  // Stop audio + mic on unmount.
  useEffect(() => () => { voiceRef.current.stop(); stt.stopListening(); }, [stt]);

  if (!entitled) return null;

  function toggleMic() {
    if (listening) {
      submit(stt.transcript);
      return;
    }
    voice.stop();
    skipResumeRef.current = false;
    lastSpokenRef.current = messages[messages.length - 1]?.messageId ?? null; // don't re-read history
    beginListening();
  }

  function toggleContinuous() {
    setContinuous((c) => {
      const next = !c;
      if (!next && listeningRef.current) {
        // turning hands-free off mid-loop — stop listening
        setListening(false);
        stt.stopListening();
      }
      return next;
    });
  }

  function stopSpeaking() {
    skipResumeRef.current = true; // don't auto-resume from a manual stop
    voice.stop();
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={toggleContinuous}
        aria-pressed={continuous}
        title={continuous ? 'Hands-free on — tap to switch to push-to-talk' : 'Hands-free conversation'}
        className={`p-2 rounded transition-colors cursor-pointer ${
          continuous
            ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
            : 'bg-dark-subtle text-content-dim border border-dark-border hover:text-content'
        }`}
      >
        <Repeat size={14} />
      </button>
      {voice.isSpeaking && (
        <button
          type="button"
          onClick={stopSpeaking}
          title="Stop speaking"
          className="p-2 rounded bg-dark-subtle text-gold-400 border border-dark-border hover:text-content transition-colors cursor-pointer"
        >
          <Volume2 size={16} className="animate-pulse" />
        </button>
      )}
      <button
        type="button"
        onClick={toggleMic}
        disabled={isLoading}
        aria-pressed={listening}
        title={listening ? 'Stop & send' : 'Talk to Professor Cogni'}
        className={`p-2 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
          listening
            ? 'bg-gold-500 text-white animate-pulse'
            : 'bg-dark-subtle text-content-muted border border-dark-border hover:text-content'
        }`}
      >
        {listening ? <Square size={16} /> : <Mic size={16} />}
      </button>
    </div>
  );
}
